import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'

import { DataTable } from '@/components/DataTable'
import { LocalDate } from '@/components/LocalDate'
import { PageHeader } from '@/components/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { type AdminContentRow, adminListContent } from '@/lib/cms/admin/server'
import { CMS_KIND_LABELS, CMS_KINDS } from '@/lib/cms/cms'
import { CONTENT_LIST_KEY } from './-components/content-editor'

export const Route = createFileRoute('/_authenticated/admin/content')({
  component: AdminContentPage,
})

const columns: ColumnDef<AdminContentRow>[] = [
  {
    accessorKey: 'title',
    header: 'Title',
    cell: ({ row }) => (
      <Link
        to="/admin/content/$id"
        params={{ id: row.original.id }}
        className="font-medium hover:underline"
      >
        {row.original.title}
      </Link>
    ),
  },
  {
    accessorKey: 'kind',
    header: 'Kind',
    enableSorting: true,
    cell: ({ row }) => (
      <Badge variant="outline">{CMS_KIND_LABELS[row.original.kind]}</Badge>
    ),
  },
  {
    accessorKey: 'slug',
    header: 'Slug',
    cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">
        {row.original.slug}
      </span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant={row.original.status === 'published' ? 'default' : 'secondary'}
      >
        {row.original.status === 'published' ? 'Published' : 'Draft'}
      </Badge>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated',
    enableSorting: true,
    // LocalDate, not formatDate: "when did I last save" is a per-viewer
    // moment, and the editorial formatter renders UTC on purpose.
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        <LocalDate iso={row.original.updatedAt} />
      </span>
    ),
  },
]

function AdminContentPage() {
  const query = useQuery({
    queryKey: CONTENT_LIST_KEY,
    queryFn: () => adminListContent(),
  })

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Content"
        description="Blog posts, docs guides, and legal pages. Drafts are only visible here; publishing puts them on the site and the content API within ~5 minutes (edge cache)."
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                <Plus data-icon="inline-start" />
                New
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {CMS_KINDS.map((kind) => (
                <DropdownMenuItem key={kind} asChild>
                  <Link to="/admin/content/new" search={{ kind }}>
                    {CMS_KIND_LABELS[kind]}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <DataTable
        columns={columns}
        data={query.data}
        isPending={query.isPending}
        emptyMessage="No documents yet — create the first one."
        pageSize={25}
      />
    </div>
  )
}
